import React, { useContext, useEffect, useRef, useState } from 'react';
import { AuthContext } from 'context/auth';
import {
  Box,
  Heading,
  useToast,
  Accordion,
  Checkbox,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Badge,
  Center,
  Tag,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Portal,
  ListItem,
  UnorderedList,
  IconButton,
  Link,
  TagLabel,
  TagLeftIcon,
  Stat,
  StatLabel,
  StatNumber,
  Spinner,
  Button,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  useDisclosure,
  MenuDivider,
  FormLabel,
  Textarea,
  Flex,
  Spacer,
  useBoolean,
  Icon,
  HStack,
  Tooltip,
} from '@chakra-ui/react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { MCFFormsContext } from 'context/mcfforms';
import { CgNotes } from 'react-icons/cg';
import { BsCheckCircle } from 'react-icons/bs';
import { TbEyeCheck } from 'react-icons/tb';
import {
  CheckIcon,
  ChevronDownIcon,
  CloseIcon,
  DeleteIcon,
  EditIcon,
  RepeatClockIcon,
  RepeatIcon,
  TimeIcon,
  WarningTwoIcon,
} from '@chakra-ui/icons';
import { Link as RouterLink } from 'react-router-dom';

export default function GeneralOverview(props) {
  const { user } = useContext(AuthContext);
  const toast = useToast();
  const {
    MCFForms,
    getMCFForms,
    partCount,
    partsPendingMentorReview,
    partsPendingReview,
    partsPendingFab,
    partsFabCompleted,
  } = useContext(MCFFormsContext);
  const [hidden, setHidden] = useState(true);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = useRef();
  const [alertDialog, setAlertDialog] = useState({
    header: '',
    body: '',
    buttonText: '',
    buttonColor: '',
    buttonAction: null,
  });
  const selectedRowData = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [accordionIndex, setAccordionIndex] = useState([]);
  const copyDebouncer = useRef(false);
  const [checkedPendingItems, setCheckedPendingItems] = useState([]);

  useEffect(() => {
    if (MCFForms) {
      let index = [];
      if (partCount.current.pendingMentorReview > 0) {
        index.push(0);
      }
      if (partCount.current.pendingReview > 0) {
        index.push(1);
      }
      if (partCount.current.pendingFab > 0) {
        index.push(2);
      }
      setAccordionIndex(index);
    }
  }, [MCFForms]);

  const handleFilePath = rowData => {
    var expression =
      /(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})/gi;
    const regex = new RegExp(expression);
    const handleOnClick = e => {
      if (rowData.filePath.match(regex)) {
        window.open(rowData.filePath, '_blank');
      } else {
        if (!copyDebouncer.current) {
          navigator.clipboard.writeText(rowData.filePath);
          toast({
            title: 'File Path copied to clipboard',
            description: rowData.filePath,
            duration: 2000,
            isClosable: true,
          });
          copyDebouncer.current = true;
          setTimeout(() => {
            copyDebouncer.current = false;
          }, 500);
        }
      }
    };
    return (
      <Tag
        size="sm"
        bgColor={rowData.rejection.rejected ? 'red' : 'gray.200'}
        onClick={handleOnClick}
        overflow={'hidden'}
        maxWidth={'300px'}
      >
        <Link>
          <Tooltip label={rowData.filePath}>{rowData.filePath}</Tooltip>
        </Link>
      </Tag>
    );
  };

  const handleFileName = rowData => {
    const handleOnClick = e => {
      if (!copyDebouncer.current) {
        navigator.clipboard.writeText(rowData.fileName);
        toast({
          title: 'File Name copied to clipboard',
          description: rowData.fileName,
          duration: 2000,
          isClosable: true,
        });
        copyDebouncer.current = true;
        setTimeout(() => {
          copyDebouncer.current = false;
        }, 500);
      }
    };

    return (
      <Tag
        size="sm"
        bgColor={rowData.rejection.rejected ? 'red' : 'gray.200'}
        onClick={handleOnClick}
        overflow={'hidden'}
      >
        <Link>{rowData.fileName}</Link>
      </Tag>
    );
  };

  const handleSubmitterName = rowData => {
    return (
      <>
        <Tag variant={'outline'}>
          {rowData.rejection.rejected && (
            <Icon as={WarningTwoIcon} color="red" />
          )}
          {rowData.firstName} {rowData.lastName.charAt(0)}.
        </Tag>
      </>
    );
  };

  const handleNotes = rowData => {
    return (
      <Popover>
        <PopoverTrigger>
          <Center>
            <IconButton
              size="sm"
              variant="outline"
              colorScheme="teal"
              aria-label="View Notes"
              icon={<CgNotes />}
            />
          </Center>
        </PopoverTrigger>
        <Portal>
          <PopoverContent>
            <PopoverArrow />
            <PopoverHeader>Notes</PopoverHeader>
            <PopoverCloseButton />
            <PopoverBody>
              <UnorderedList>
                {rowData.notes.length !== 0 ? (
                  rowData.notes
                    .slice(0)
                    .reverse()
                    .map((e, i) => {
                      return (
                        <ListItem key={i}>
                          <Stat>
                            <StatLabel>
                              <Badge colorScheme="teal">
                                {new Date(e.timestamp).toLocaleString('en-US', {
                                  day: 'numeric', // numeric, 2-digit
                                  year: 'numeric', // numeric, 2-digit
                                  month: 'short', // numeric, 2-digit, long, short, narrow
                                  hour: 'numeric', // numeric, 2-digit
                                  minute: 'numeric', // numeric, 2-digit
                                  hour12: true,
                                })}
                              </Badge>
                            </StatLabel>
                            <StatNumber fontSize={'sm'}>{e.note}</StatNumber>
                          </Stat>
                        </ListItem>
                      );
                    })
                ) : (
                  <ListItem>No notes</ListItem>
                )}
              </UnorderedList>
            </PopoverBody>
          </PopoverContent>
        </Portal>
      </Popover>
    );
  };

  const handlePriority = rowData => {
    switch (rowData.priority) {
      case 1:
        return (
          <Tag size="sm" colorScheme="red">
            National Emergency
          </Tag>
        );
        break;
      case 2:
        return (
          <Tag size="sm" colorScheme="orange">
            High
          </Tag>
        );
        break;
      case 3:
        return (
          <Tag size="sm" colorScheme="yellow">
            Medium
          </Tag>
        );
        break;
      case 4:
        return (
          <Tag size="sm" colorScheme="purple">
            Low
          </Tag>
        );
        break;
      default:
        'Undefined';
    }
  };

  const handleRerun = rowData => {
    return (
      <Center>
        <IconButton
          size="sm"
          variant="outline"
          colorScheme="blue"
          aria-label="Edit"
          icon={<RepeatIcon />}
          as={RouterLink}
          to={`/submitForRerun/${rowData._id}`}
        />
      </Center>
    );
  };

  const errorToast = () => {
    toast({
      title: 'Error',
      description: 'There was an error processing your request',
      status: 'error',
      duration: 5000,
      isClosable: true,
    });
  };

  const handleDelete = e => {
    e.target.disabled = true;
    let objectId = selectedRowData.current._id;
    fetch(`/transactions/deleteMCFForm/${objectId}`, {
      method: 'GET',
    })
      .then(response => {
        if (response.status !== 200) {
          throw `${response.status}: ${response.statusText}`;
        }
        return response.text();
      })
      .then(function (data) {
        // Success
        getMCFForms(() => {
          onClose();
          toast({
            title: 'Part MCF Submission Deleted',
            status: 'success',
            duration: 5000,
            isClosable: true,
          });
        });
      })
      .catch(err => {
        console.log(err);
        e.target.disabled = false;
        errorToast();
      });
  };

  const handleApprove = (e, rowData) => {
    e.target.disabled = true;
    let objectId = selectedRowData.current._id;
    let updatedAt = selectedRowData.current.updatedAt;
    fetch(`/transactions/getMCFForm/${objectId}`, {
      method: 'GET',
    })
      .then(response => {
        if (response.status !== 200) {
          throw `${response.status}: ${response.statusText}`;
        }
        return response.json();
      })
      .then(data => {
        if (
          new Date(updatedAt).getTime() ===
          new Date(data.docs.updatedAt).getTime()
        ) {
          let approvalUrl = data.docs.mentorApproved
            ? `/transactions/approveMCFForm/${objectId}?approved=true`
            : `/transactions/approveMCFForm/${objectId}?mentorApproved=true`;

          fetch(approvalUrl, {
            method: 'GET',
          })
            .then(response => {
              if (response.status !== 200) {
                throw `${response.status}: ${response.statusText}`;
              }
              return response.text();
            })
            .then(data => {
              getMCFForms(() => {
                onClose();
                toast({
                  title: 'Part MCF Approved',
                  status: 'info',
                  duration: 5000,
                  isClosable: true,
                });
              });
            })
            .catch(err => {
              e.target.disabled = false;
              console.error(err);
              errorToast();
            });
        } else {
          e.target.disabled = false;
          toast({
            title: 'Error',
            description: 'Current document is outdated. Please refresh.',
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
        }
      })
      .catch(err => {
        e.target.disabled = false;
        console.error(err);
        errorToast();
      });
  };

  const handleSendBack = e => {
    e.target.disabled = true;
    let objectId = selectedRowData.current._id;
    let updatedAt = selectedRowData.current.updatedAt;

    fetch(`/transactions/getMCFForm/${objectId}`, {
      method: 'GET',
    })
      .then(response => {
        if (response.status !== 200) {
          throw `${response.status}: ${response.statusText}`;
        }
        return response.json();
      })
      .then(data => {
        if (
          new Date(updatedAt).getTime() ===
          new Date(data.docs.updatedAt).getTime()
        ) {
          fetch(`/transactions/approveMCFForm/${objectId}?approved=false`, {
            method: 'GET',
          })
            .then(response => {
              if (response.status !== 200) {
                throw `${response.status}: ${response.statusText}`;
              }
              return response.text();
            })
            .then(data => {
              getMCFForms(() => {
                onClose();
                toast({
                  title: 'Part MCF sent back to review',
                  status: 'warning',
                  duration: 5000,
                  isClosable: true,
                });
              });
            })
            .catch(err => {
              e.target.disabled = false;
              console.error(err);
              errorToast();
            });
        } else {
          toast({
            title: 'Error',
            description: 'Current document is outdated. Please refresh.',
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
        }
      })
      .catch(err => {
        e.target.disabled = false;
        console.error(err);
        errorToast();
      });
  };

  const handleMarkCompleted = e => {
    e.target.disabled = true;
    let objectId = selectedRowData.current._id;
    let updatedAt = selectedRowData.current.updatedAt;

    fetch(`/transactions/getMCFForm/${objectId}`, {
      method: 'GET',
    })
      .then(response => {
        if (response.status !== 200) {
          throw `${response.status}: ${response.statusText}`;
        }
        return response.json();
      })
      .then(data => {
        if (
          new Date(updatedAt).getTime() ===
          new Date(data.docs.updatedAt).getTime()
        ) {
          fetch(
            `/transactions/completedMCFForm/${objectId}?manufactured=true`,
            {
              method: 'GET',
            }
          )
            .then(response => {
              if (response.status !== 200) {
                throw `${response.status}: ${response.statusText}`;
              }
              return response.text();
            })
            .then(data => {
              getMCFForms(() => {
                onClose();
                toast({
                  title: 'Part MCF marked as completed',
                  status: 'success',
                  duration: 5000,
                  isClosable: true,
                });
              });
            })
            .catch(err => {
              console.error(err);
              errorToast();
            });
        } else {
          toast({
            title: 'Error',
            description: 'Current document is outdated. Please refresh.',
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
        }
      })
      .catch(err => {
        console.error(err);
      });
  };

  const handleMultiMarkCompleted = e => {
    e.preventDefault();
    e.target.disabled = true;
    let pendingItems = [...checkedPendingItems];

    for (let rowData of checkedPendingItems) {
      let objectId = rowData._id;
      let updatedAt = rowData.updatedAt;

      fetch(`/transactions/getMCFForm/${objectId}`, {
        method: 'GET',
      })
        .then(response => {
          if (response.status !== 200) {
            throw `${response.status}: ${response.statusText}`;
          }
          return response.json();
        })
        .then(data => {
          if (
            new Date(updatedAt).getTime() ===
            new Date(data.docs.updatedAt).getTime()
          ) {
            fetch(
              `/transactions/completedMCFForm/${objectId}?manufactured=true`,
              {
                method: 'GET',
              }
            )
              .then(response => {
                if (response.status !== 200) {
                  throw `${response.status}: ${response.statusText}`;
                }
                return response.text();
              })
              .then(data => {
                getMCFForms(() => {
                  onClose();
                  pendingItems.splice(
                    pendingItems.findIndex(({ _id }) => _id === objectId),
                    1
                  );
                  toast({
                    title: 'Part MCF marked as completed',
                    status: 'success',
                    duration: 5000,
                    isClosable: true,
                  });
                });
              })
              .catch(err => {
                console.error(err);
                errorToast();
              });
          } else {
            toast({
              title: 'Error',
              description: 'Current document is outdated. Please refresh.',
              status: 'error',
              duration: 5000,
              isClosable: true,
            });
          }
        })
        .catch(err => {
          console.error(err);
        });
    }
    setCheckedPendingItems(pendingItems);
    e.target.disabled = false;
  };

  const handleApproveOnClick = rowData => {
    selectedRowData.current = rowData;
    setAlertDialog({
      header: rowData.fileName,
      body: 'Are you sure you want to approve this part?',
      buttonText: 'Approve',
      buttonColor: 'blue',
      buttonAction: handleApprove,
    });
    onOpen();
  };

  const handleSendBackOnClick = rowData => {
    selectedRowData.current = rowData;
    setAlertDialog({
      header: rowData.fileName,
      body: 'Are you sure you want to send this part back to review?',
      buttonText: 'Send back',
      buttonColor: 'orange',
      buttonAction: handleSendBack,
    });
    onOpen();
  };

  const handleMarkCompletedOnClick = rowData => {
    selectedRowData.current = rowData;
    setAlertDialog({
      header: rowData.fileName,
      body: 'Are you sure you want to mark this part as completed?',
      buttonText: 'Completed',
      buttonColor: 'green',
      buttonAction: handleMarkCompleted,
    });
    onOpen();
  };

  const handleDeleteOnClick = rowData => {
    selectedRowData.current = rowData;
    setAlertDialog({
      header: rowData.fileName,
      body: "Are you sure? You can't undo this action afterwards.",
      buttonText: 'Delete',
      buttonColor: 'red',
      buttonAction: handleDelete,
    });
    onOpen();
  };

  const handleReview = rowData => {
    return (
      <Menu strategy={'fixed'}>
        <Center>
          <MenuButton size="sm" as={Button} rightIcon={<ChevronDownIcon />}>
            Actions
          </MenuButton>
        </Center>
        <MenuList>
          {rowData.approved && (
            <MenuItem
              icon={<CheckIcon />}
              onClick={() => handleMarkCompletedOnClick(rowData)}
            >
              Mark as Completed
            </MenuItem>
          )}
          {!rowData.approved ? (
            <>
              {!rowData.rejection.rejected && (user.admin || user.mentor) && (
                <MenuItem
                  icon={<CheckIcon />}
                  onClick={() => handleApproveOnClick(rowData)}
                >
                  Approve
                </MenuItem>
              )}
              <MenuItem
                icon={<CloseIcon />}
                as={RouterLink}
                to={`/rejectForm/${rowData._id}`}
              >
                Reject
              </MenuItem>{' '}
            </>
          ) : (
            <MenuItem
              icon={<RepeatClockIcon />}
              onClick={() => handleSendBackOnClick(rowData)}
            >
              Send back to review
            </MenuItem>
          )}
          <MenuDivider />
          {!rowData.approved ? (
            <MenuItem
              icon={<EditIcon />}
              as={RouterLink}
              to={`/editForm/${rowData._id}`}
            >
              Edit
            </MenuItem>
          ) : (
            <MenuItem
              icon={<EditIcon />}
              as={RouterLink}
              to={`/editApprovedForm/${rowData._id}`}
            >
              Edit
            </MenuItem>
          )}

          <MenuItem
            icon={<DeleteIcon />}
            onClick={() => handleDeleteOnClick(rowData)}
          >
            Delete
          </MenuItem>
        </MenuList>
      </Menu>
    );
  };

  const handleRefresh = e => {
    setIsLoading(true);
    setTimeout(() => {
      getMCFForms(() => {
        setIsLoading(false);
        toast({
          title: 'Data refreshed',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      });
    }, 2000);
  };

  const subsystems = [
    'Chassis',
    'Climb',
    'Drivetrain',
    'Elevator',
    'Feeder',
    'Hopper',
    'Intake',
    'Shooter',
    'Scoring Delivery',
    'Miscellaneous',
  ];

  const subsystemFilterTemplate = options => {
    let filteredSubsystems = MCFForms.map(e => e.subsystem);
    filteredSubsystems = subsystems.filter(subsystem =>
      filteredSubsystems.includes(subsystem)
    );
    return (
      <Dropdown
        value={options.value}
        options={filteredSubsystems}
        onChange={e => options.filterCallback(e.value, options.index)}
        itemTemplate={option => {
          return <span className="image-text">{option}</span>;
        }}
        placeholder="Select a Subsystem"
        showClear
      />
    );
  };

  const mfrProcesses = ['Hurco CNC', 'Laser Cut', 'Water Jet', 'Bend'];

  const mfrProcessesFilterTemplate = options => {
    return (
      <Dropdown
        value={options.value}
        options={mfrProcesses}
        onChange={e => options.filterCallback(e.value, options.index)}
        itemTemplate={option => {
          return <span className="image-text">{option}</span>;
        }}
        placeholder="Select a machine"
        showClear
      />
    );
  };

  if (MCFForms === null) {
    return (
      <>
        <Box
          margin={'0 auto'}
          mt={'1em'}
          width={{ base: '85%', md: '66%', lg: '50%' }}
        >
          <Center>
            <Spinner mt={'2em'} size="lg" />
          </Center>
        </Box>
      </>
    );
  }
  return (
    <>
      <Box
        margin={'0 auto'}
        mt={'1em'}
        width={{ base: '100%', md: '98%', lg: '95%' }}
        p={6}
        borderWidth="1px"
        rounded="lg"
        shadow="1px 1px 3px rgba(0,0,0,0.3)"
        bgColor={'#fff'}
      >
        <Flex alignItems="center" gap="2">
          <Box p="2">
            <Heading mb={2} as="h1" size="lg">
              General Overview{' '}
              {user.admin ? (
                <Badge>Reviewer</Badge>
              ) : user.mentor ? (
                <Badge>Mentor</Badge>
              ) : (
                <Badge>Submitter</Badge>
              )}
            </Heading>
          </Box>
          <Spacer />
          <Button
            leftIcon={<RepeatIcon />}
            isLoading={isLoading}
            loadingText="Refreshing"
            onClick={handleRefresh}
            size="lg"
            colorScheme="gray"
            textColor="teal"
            variant="solid"
          >
            Refresh
          </Button>
        </Flex>
        <Accordion
          mt={'1em'}
          allowMultiple
          onChange={index =>
            setAccordionIndex(
              // @ts-ignore
              index
            )
          }
          index={accordionIndex}
        >
          <AccordionItem>
            <h2>
              <AccordionButton>
                <Box as="span" flex="1" textAlign="left">
                  <Tag size={'lg'} variant="subtle" colorScheme="red">
                    <TagLeftIcon boxSize="14px" as={TimeIcon} />
                    <TagLabel>
                      {partCount.current.pendingMentorReview} Pending Mentor
                      Review
                    </TagLabel>
                  </Tag>
                </Box>
                <AccordionIcon />
              </AccordionButton>
            </h2>
            <AccordionPanel pb={4}>
              <DataTable
                sortField="priority"
                sortOrder={1}
                value={partsPendingMentorReview}
                showGridlines
                responsiveLayout="scroll"
                size="small"
              >
                <Column
                  field="submitter"
                  header="Submitter"
                  body={handleSubmitterName}
                />
                <Column
                  field="subsystem"
                  header="Subsystem"
                  style={{ width: '8%' }}
                  body={rowData => (
                    <Tag
                      size="sm"
                      bgColor={rowData.rejection.rejected ? 'red' : 'gray.200'}
                    >
                      {rowData.subsystem}
                    </Tag>
                  )}
                  showFilterMatchModes={false}
                  showFilterMenuOptions={false}
                  filter
                  filterField="subsystem"
                  filterElement={subsystemFilterTemplate}
                />
                <Column
                  field="filePath"
                  header="File Path"
                  style={{ width: '20%' }}
                  body={handleFilePath}
                  filterMatchMode="contains"
                  showFilterMatchModes={false}
                  showFilterMenuOptions={false}
                  filter
                  filterPlaceholder="Search by file path"
                />
                <Column
                  field="fileName"
                  header="File Name"
                  body={handleFileName}
                  filterMatchMode="contains"
                  showFilterMatchModes={false}
                  showFilterMenuOptions={false}
                  filter
                  filterPlaceholder="Search by file name"
                />
                <Column
                  field="revision"
                  header="Revision"
                  style={{ width: '5%' }}
                  body={rowData => (
                    <Tag
                      size="sm"
                      bgColor={rowData.rejection.rejected ? 'red' : 'gray.200'}
                    >
                      {rowData.revision}
                    </Tag>
                  )}
                />
                <Column
                  field="material"
                  header="Material"
                  body={rowData => (
                    <Tag
                      size="sm"
                      bgColor={rowData.rejection.rejected ? 'red' : 'gray.200'}
                    >
                      {rowData.material}
                    </Tag>
                  )}
                />
                <Column
                  field="mfrProcess"
                  header="Machine(s)"
                  style={{ width: '12%' }}
                  body={rowData => (
                    <Tag
                      size="sm"
                      bgColor={rowData.rejection.rejected ? 'red' : 'gray.200'}
                    >
                      {rowData.mfrProcess.length === 1
                        ? rowData.mfrProcess[0]
                        : rowData.mfrProcess.join(', ')}
                    </Tag>
                  )}
                  filterMatchMode="contains"
                  showFilterMatchModes={false}
                  showFilterMenuOptions={false}
                  filter
                  filterField="mfrProcess"
                  filterElement={mfrProcessesFilterTemplate}
                />
                <Column
                  field="finish"
                  header="Finish"
                  body={rowData => (
                    <Tag
                      size="sm"
                      bgColor={rowData.rejection.rejected ? 'red' : 'gray.200'}
                    >
                      {rowData.finish}
                    </Tag>
                  )}
                />
                <Column
                  field="quantity"
                  header="Quantity"
                  style={{ width: '5%' }}
                  body={rowData => (
                    <Tag
                      size="sm"
                      bgColor={rowData.rejection.rejected ? 'red' : 'gray.200'}
                    >
                      {rowData.quantity}
                    </Tag>
                  )}
                />
                <Column
                  field="priority"
                  header="Priority"
                  style={{ width: '5%' }}
                  body={handlePriority}
                  sortable
                  sortableDisabled
                />
                <Column field="notes" header="Notes" body={handleNotes} />
                {(user.admin || user.mentor) && (
                  <Column header="Review" body={handleReview} />
                )}
              </DataTable>
            </AccordionPanel>
          </AccordionItem>

          <AccordionItem>
            <h2>
              <AccordionButton>
                <Box as="span" flex="1" textAlign="left">
                  <Tag size={'lg'} variant="subtle" colorScheme="red">
                    <TagLeftIcon boxSize="14px" as={TimeIcon} />
                    <TagLabel>
                      {partCount.current.pendingReview} Pending Boyce Review
                    </TagLabel>
                  </Tag>
                </Box>
                <AccordionIcon />
              </AccordionButton>
            </h2>
            <AccordionPanel pb={4}>
              <DataTable
                sortField="priority"
                sortOrder={1}
                value={partsPendingReview}
                showGridlines
                responsiveLayout="scroll"
                size="small"
              >
                <Column
                  field="submitter"
                  header="Submitter"
                  body={handleSubmitterName}
                />
                <Column
                  field="subsystem"
                  header="Subsystem"
                  style={{ width: '8%' }}
                  body={rowData => (
                    <Tag
                      size="sm"
                      bgColor={rowData.rejection.rejected ? 'red' : 'gray.200'}
                    >
                      {rowData.subsystem}
                    </Tag>
                  )}
                  showFilterMatchModes={false}
                  showFilterMenuOptions={false}
                  filter
                  filterField="subsystem"
                  filterElement={subsystemFilterTemplate}
                />
                <Column
                  field="filePath"
                  header="File Path"
                  style={{ width: '20%' }}
                  body={handleFilePath}
                  filterMatchMode="contains"
                  showFilterMatchModes={false}
                  showFilterMenuOptions={false}
                  filter
                  filterPlaceholder="Search by file path"
                />
                <Column
                  field="fileName"
                  header="File Name"
                  body={handleFileName}
                  filterMatchMode="contains"
                  showFilterMatchModes={false}
                  showFilterMenuOptions={false}
                  filter
                  filterPlaceholder="Search by file name"
                />
                <Column
                  field="revision"
                  header="Revision"
                  style={{ width: '5%' }}
                  body={rowData => (
                    <Tag
                      size="sm"
                      bgColor={rowData.rejection.rejected ? 'red' : 'gray.200'}
                    >
                      {rowData.revision}
                    </Tag>
                  )}
                />
                <Column
                  field="material"
                  header="Material"
                  body={rowData => (
                    <Tag
                      size="sm"
                      bgColor={rowData.rejection.rejected ? 'red' : 'gray.200'}
                    >
                      {rowData.material}
                    </Tag>
                  )}
                />
                <Column
                  field="mfrProcess"
                  header="Machine(s)"
                  style={{ width: '12%' }}
                  body={rowData => (
                    <Tag
                      size="sm"
                      bgColor={rowData.rejection.rejected ? 'red' : 'gray.200'}
                    >
                      {rowData.mfrProcess.length === 1
                        ? rowData.mfrProcess[0]
                        : rowData.mfrProcess.join(', ')}
                    </Tag>
                  )}
                  filterMatchMode="contains"
                  showFilterMatchModes={false}
                  showFilterMenuOptions={false}
                  filter
                  filterField="mfrProcess"
                  filterElement={mfrProcessesFilterTemplate}
                />
                <Column
                  field="finish"
                  header="Finish"
                  body={rowData => (
                    <Tag
                      size="sm"
                      bgColor={rowData.rejection.rejected ? 'red' : 'gray.200'}
                    >
                      {rowData.finish}
                    </Tag>
                  )}
                />
                <Column
                  field="quantity"
                  header="Quantity"
                  style={{ width: '5%' }}
                  body={rowData => (
                    <Tag
                      size="sm"
                      bgColor={rowData.rejection.rejected ? 'red' : 'gray.200'}
                    >
                      {rowData.quantity}
                    </Tag>
                  )}
                />
                <Column
                  field="priority"
                  header="Priority"
                  style={{ width: '5%' }}
                  body={handlePriority}
                  sortable
                  sortableDisabled
                />
                <Column field="notes" header="Notes" body={handleNotes} />
                {user.admin && <Column header="Review" body={handleReview} />}
              </DataTable>
            </AccordionPanel>
          </AccordionItem>

          <AccordionItem>
            <h2>
              <AccordionButton as="div" cursor={'pointer'}>
                <HStack flex="1" textAlign="left">
                  <Tag size={'lg'} variant="subtle" colorScheme="orange">
                    <TagLeftIcon boxSize="14px" as={TbEyeCheck} />
                    <TagLabel>
                      {partCount.current.pendingFab} Pending Fabrication
                    </TagLabel>
                  </Tag>
                  {checkedPendingItems.length > 0 && (
                    <>
                      <Tag size={'lg'} variant="subtle">
                        <TagLabel>
                          {checkedPendingItems.length} Selected
                        </TagLabel>
                      </Tag>
                      <Button
                        size="sm"
                        colorScheme="teal"
                        onClick={handleMultiMarkCompleted}
                      >
                        Mark as Completed
                      </Button>
                    </>
                  )}
                </HStack>
                <AccordionIcon />
              </AccordionButton>
            </h2>
            <AccordionPanel pb={4}>
              <DataTable
                sortField="priority"
                sortOrder={1}
                value={partsPendingFab}
                showGridlines
                responsiveLayout="scroll"
                size="small"
              >
                {user.admin && (
                  <Column
                    align="center"
                    header="Select"
                    body={rowData => {
                      return (
                        <Checkbox
                          isChecked={checkedPendingItems.some(
                            el => el._id === rowData._id
                          )}
                          onChange={() => {
                            let newCheckedPendingItems = [
                              ...checkedPendingItems,
                            ];
                            let itemIndex = newCheckedPendingItems.findIndex(
                              el => el._id === rowData._id
                            );
                            if (itemIndex !== -1) {
                              newCheckedPendingItems.splice(itemIndex, 1);
                            } else {
                              newCheckedPendingItems.push(rowData);
                            }
                            setCheckedPendingItems(newCheckedPendingItems);
                          }}
                        ></Checkbox>
                      );
                    }}
                  />
                )}
                <Column
                  field="submitter"
                  header="Submitter"
                  body={handleSubmitterName}
                />
                <Column
                  field="subsystem"
                  header="Subsystem"
                  style={{ width: '8%' }}
                  body={rowData => <Tag size="sm">{rowData.subsystem}</Tag>}
                  showFilterMatchModes={false}
                  showFilterMenuOptions={false}
                  filter
                  filterField="subsystem"
                  filterElement={subsystemFilterTemplate}
                />
                <Column
                  field="filePath"
                  header="File Path"
                  style={{ width: '20%' }}
                  body={handleFilePath}
                  filterMatchMode="contains"
                  showFilterMatchModes={false}
                  showFilterMenuOptions={false}
                  filter
                  filterPlaceholder="Search by file path"
                />
                <Column
                  field="fileName"
                  header="File Name"
                  body={handleFileName}
                  filterMatchMode="contains"
                  showFilterMatchModes={false}
                  showFilterMenuOptions={false}
                  filter
                  filterPlaceholder="Search by file name"
                />
                <Column
                  field="revision"
                  header="Revision"
                  style={{ width: '5%' }}
                  body={rowData => <Tag size="sm">{rowData.revision}</Tag>}
                />
                <Column
                  field="material"
                  header="Material"
                  body={rowData => <Tag size="sm">{rowData.material}</Tag>}
                />
                <Column
                  field="mfrProcess"
                  header="Machine(s)"
                  style={{ width: '12%' }}
                  body={rowData => (
                    <Tag size="sm">
                      {rowData.mfrProcess.length === 1
                        ? rowData.mfrProcess[0]
                        : rowData.mfrProcess.join(', ')}
                    </Tag>
                  )}
                  filterMatchMode="contains"
                  showFilterMatchModes={false}
                  showFilterMenuOptions={false}
                  filter
                  filterField="mfrProcess"
                  filterElement={mfrProcessesFilterTemplate}
                />
                <Column
                  field="finish"
                  header="Finish"
                  body={rowData => <Tag size="sm">{rowData.finish}</Tag>}
                />
                <Column
                  field="quantity"
                  header="Quantity"
                  style={{ width: '5%' }}
                  body={rowData => <Tag size="sm">{rowData.quantity}</Tag>}
                />
                <Column
                  field="priority"
                  header="Priority"
                  style={{ width: '5%' }}
                  body={handlePriority}
                  sortable
                  sortableDisabled
                />
                <Column field="notes" header="Notes" body={handleNotes} />
                {user.admin && <Column header="Review" body={handleReview} />}
              </DataTable>
            </AccordionPanel>
          </AccordionItem>

          <AccordionItem>
            <h2>
              <AccordionButton>
                <Box as="span" flex="1" textAlign="left">
                  <Tag size={'lg'} variant="subtle" colorScheme="green">
                    <TagLeftIcon boxSize="14px" as={BsCheckCircle} />
                    <TagLabel>
                      {partCount.current.fabCompleted} Fabrication Completed
                    </TagLabel>
                  </Tag>
                </Box>
                <AccordionIcon />
              </AccordionButton>
            </h2>
            <AccordionPanel pb={4}>
              {accordionIndex.includes(3) && (
                <DataTable
                  value={partsFabCompleted}
                  showGridlines
                  responsiveLayout="scroll"
                  size="small"
                >
                  <Column
                    field="submitter"
                    header="Submitter"
                    body={handleSubmitterName}
                  />
                  <Column
                    field="subsystem"
                    header="Subsystem"
                    style={{ width: '8%' }}
                    body={rowData => <Tag size="sm">{rowData.subsystem}</Tag>}
                    showFilterMatchModes={false}
                    showFilterMenuOptions={false}
                    filter
                    filterField="subsystem"
                    filterElement={subsystemFilterTemplate}
                  />
                  <Column
                    field="filePath"
                    header="File Path"
                    style={{ width: '20%' }}
                    body={handleFilePath}
                    filterMatchMode="contains"
                    showFilterMatchModes={false}
                    showFilterMenuOptions={false}
                    filter
                    filterPlaceholder="Search by file path"
                  />
                  <Column
                    field="fileName"
                    header="File Name"
                    body={handleFileName}
                    filterMatchMode="contains"
                    showFilterMatchModes={false}
                    showFilterMenuOptions={false}
                    filter
                    filterPlaceholder="Search by file name"
                  />
                  <Column
                    field="revision"
                    header="Revision"
                    style={{ width: '5%' }}
                    body={rowData => <Tag size="sm">{rowData.revision}</Tag>}
                  />
                  <Column
                    field="material"
                    header="Material"
                    body={rowData => <Tag size="sm">{rowData.material}</Tag>}
                  />
                  <Column
                    field="mfrProcess"
                    header="Machine(s)"
                    style={{ width: '12%' }}
                    body={rowData => (
                      <Tag size="sm">
                        {rowData.mfrProcess.length === 1
                          ? rowData.mfrProcess[0]
                          : rowData.mfrProcess.join(', ')}
                      </Tag>
                    )}
                    filterMatchMode="contains"
                    showFilterMatchModes={false}
                    showFilterMenuOptions={false}
                    filter
                    filterField="mfrProcess"
                    filterElement={mfrProcessesFilterTemplate}
                  />
                  <Column
                    field="finish"
                    header="Finish"
                    body={rowData => <Tag size="sm">{rowData.finish}</Tag>}
                  />
                  <Column
                    field="quantity"
                    header="Quantity"
                    style={{ width: '5%' }}
                    body={rowData => <Tag size="sm">{rowData.quantity}</Tag>}
                  />
                  <Column
                    field="priority"
                    header="Priority"
                    style={{ width: '5%' }}
                    body={handlePriority}
                    sortable
                    sortableDisabled
                  />
                  <Column field="notes" header="Notes" body={handleNotes} />
                  <Column header="Rerun" body={handleRerun} />
                </DataTable>
              )}
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      </Box>
      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {alertDialog.header}
            </AlertDialogHeader>

            <AlertDialogBody>{alertDialog.body}</AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose}>
                Cancel
              </Button>
              <Button
                colorScheme={alertDialog.buttonColor}
                onClick={alertDialog.buttonAction}
                ml={3}
              >
                {alertDialog.buttonText}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
}
